import { Badge } from '../components/common/Badge';

export const renderBooleanBadge = (
  value: boolean,
  trueLabel: string = 'Active',
  falseLabel: string = 'Disabled',
  falseVariant: 'warning' | 'error' = 'warning'
) => {
  return (
    <Badge
      label={value ? trueLabel : falseLabel}
      variant={value ? 'success' : falseVariant}
    />
  );
};

export const getContrastColor = (hexColor: string): string => {
  // Remove the hash if it exists
  const color = hexColor.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

export const truncateText = (text: string, maxLength: number = 50): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

export const getBadgeColors = (
  baseHex: string,
  bgAlpha = 0.18,
  shade = 0.35
): { bg: string; text: string } => {
  // Clamp channel to [0,255]
  const clamp = (n: number) => Math.max(0, Math.min(255, n));

  // Normalize "#RGB" → "#RRGGBB" and parse to integer channels.
  const hexToRgb = (hex: string) => {
    const h = hex.replace('#', '');
    const s = h.length === 3 ? h.split('').map(c => c + c).join('') : h.slice(0, 6);
    return {
      r: parseInt(s.slice(0, 2), 16),
      g: parseInt(s.slice(2, 4), 16),
      b: parseInt(s.slice(4, 6), 16),
    };
  };

  // Pack r,g,b back to "#RRGGBB".
  const rgbToHex = (r: number, g: number, b: number) =>
    `#${[r, g, b].map(v => clamp(Math.round(v)).toString(16).padStart(2, '0')).join('')}`;

  // Move the base color toward black by factor "shade" (0→no change, 1→black).
  const mixToward = (r: number, g: number, b: number, t: number) => ({
    r: r + (0 - r) * t,
    g: g + (0 - g) * t,
    b: b + (0 - b) * t,
  });

  try {
    const { r, g, b } = hexToRgb(baseHex);

    // Light background tint uses the same hue with low alpha.
    const bg = `rgba(${r}, ${g}, ${b}, ${bgAlpha})`;

    // Text is a darker shade of the same hue for visual harmony.
    const d = mixToward(r, g, b, shade); // darker same hue
    const text = rgbToHex(d.r, d.g, d.b);

    return { bg, text };
  } catch {
    return { bg: 'rgba(100,116,139,0.18)', text: '#334155' }; // fallback
  }
};
